import {transparentize} from 'polished';
import {DefaultTheme} from 'styled-components';

import {toReadonly} from '../../../core/src/helpers/typescript/typeHelpers';
import {PaletteColorsEnum} from '../../../core/src/models/api/paletteModel';
import {colorPrimitive} from './primitives/colorPrimitive';

/*
 * Brand.
 */

export const brandColor = colorPrimitive.blue[1000];

/*
 * Greys.
 */

/** Shades of grey. Light theme. */
export const greys = toReadonly({
  shade10: colorPrimitive.gray[50],
  shade20: colorPrimitive.gray[100],
  shade30: colorPrimitive.gray[200],
  shade40: colorPrimitive.gray[300],
  shade50: colorPrimitive.gray[500],
  shade60: colorPrimitive.gray[600],
  shade70: colorPrimitive.gray[700],
  shade80: colorPrimitive.gray[800],
  shade90: colorPrimitive.gray[1000],

  white: colorPrimitive.gray.white,
  black: colorPrimitive.gray.black,
});

/** Shades of grey. Dark theme. */
export const greysDark = toReadonly({
  white: colorPrimitive.gray.black,
  shade10: colorPrimitive.gray.black,
  shade20: colorPrimitive.gray[950],
  shade30: colorPrimitive.gray[900],
  shade40: colorPrimitive.gray[800],
  shade50: colorPrimitive.gray[600],
  shade60: colorPrimitive.gray[500],
  shade70: colorPrimitive.gray[400],
  shade80: colorPrimitive.gray[300],
  shade90: colorPrimitive.gray[200],
  shade100: colorPrimitive.gray.white,
  black: colorPrimitive.gray.white,
});

/** Shades of transparent greys. Light theme. */
export const alphas = toReadonly({
  transparent: 'transparent',
  gray10: transparentize(0.9, '#5d6985'),
  gray20: transparentize(0.82, '#5d6985'), // "selected state Gray 18%" in Figma.
  gray90: transparentize(0.1, '#5d6985'),
  black10: alphaBlack(0.04),
  black20: alphaBlack(0.08),
  black30: alphaBlack(0.1),
  black40: alphaBlack(0.15),
  black50: alphaBlack(0.3),
  black60: alphaBlack(0.6),
  black70: alphaBlack(0.7),
  white10: alphaWhite(0.08),
  white20: alphaWhite(0.15),
  white30: alphaWhite(0.3),
  white40: alphaWhite(0.6),
  white50: alphaWhite(0.8),
  white60: alphaWhite(0.95),
});

/** Shades of transparent greys. Dark theme. */
export const alphasDark = toReadonly({
  transparent: 'transparent',
  gray10: transparentize(0.9, '#B6BFC8'),
  gray20: transparentize(0.82, '#B6BFC8'),
  gray90: transparentize(0.1, '#B6BFC8'),
  white10: alphaBlack(0.04),
  white20: alphaBlack(0.08),
  white30: alphaBlack(0.1),
  white40: alphaBlack(0.15),
  white50: alphaBlack(0.3),
  white60: alphaBlack(0.6),
  black10: alphaWhite(0.08),
  black20: alphaWhite(0.15),
  black30: alphaWhite(0.3),
  black40: alphaWhite(0.6),
  black50: alphaWhite(0.8),
  black60: alphaWhite(0.95),
  black70: alphaBlack(0.7),
});

/**
 * Convenience helper to get a rgba(black, N) equivalent
 * @param n opacity (from 0 to 1)
 */
function alphaBlack(n: number) {
  return `rgba(0, 0, 0, ${n})`;
}

/**
 * Convenience helper to get a rgba(white, N) equivalent
 * @param n opacity (from 0 to 1)
 */
export function alphaWhite(n: number) {
  return `rgba(255, 255, 255, ${n})`;
}

export interface PaletteColor {
  readonly shade10: string;
  readonly shade20: string;
  readonly shade30: string;
  readonly shade40: string;
  readonly shade50: string;
}

type Palette = {readonly [T in PaletteColorsEnum]: PaletteColor};

/** Main color palette. Light theme. */
export const palette: Palette = {
  [PaletteColorsEnum.GREY]: {
    shade10: greys.shade10,
    shade20: greys.shade20,
    shade30: greys.shade30,
    shade40: greys.shade70,
    shade50: greys.shade80,
  },
  [PaletteColorsEnum.PINK]: {
    shade10: colorPrimitive.magenta[100],
    shade20: colorPrimitive.magenta[200],
    shade30: colorPrimitive.magenta[300],
    shade40: colorPrimitive.magenta[600],
    shade50: colorPrimitive.magenta[700],
  },
  [PaletteColorsEnum.RED]: {
    shade10: colorPrimitive.red[100],
    shade20: colorPrimitive.red[200],
    shade30: colorPrimitive.red[300],
    shade40: colorPrimitive.red[600],
    shade50: colorPrimitive.red[700],
  },
  [PaletteColorsEnum.ORANGE]: {
    shade10: colorPrimitive.orange[100],
    shade20: colorPrimitive.orange[200],
    shade30: colorPrimitive.orange[300],
    shade40: colorPrimitive.orange[500],
    shade50: colorPrimitive.orange[600],
  },
  [PaletteColorsEnum.YELLOW]: {
    shade10: colorPrimitive.yellow[100],
    shade20: colorPrimitive.yellow[200],
    shade30: colorPrimitive.yellow[300],
    shade40: colorPrimitive.yellow[500],
    shade50: colorPrimitive.yellow[700],
  },
  [PaletteColorsEnum.GREEN]: {
    shade10: colorPrimitive.green[100],
    shade20: colorPrimitive.green[200],
    shade30: colorPrimitive.green[300],
    shade40: colorPrimitive.green[500],
    shade50: colorPrimitive.green[600],
  },
  [PaletteColorsEnum.TEAL]: {
    shade10: colorPrimitive.teal[100],
    shade20: colorPrimitive.teal[200],
    shade30: colorPrimitive.teal[300],
    shade40: colorPrimitive.teal[500],
    shade50: colorPrimitive.teal[600],
  },
  [PaletteColorsEnum.BLUE]: {
    shade10: colorPrimitive.blue[100],
    shade20: colorPrimitive.blue[200],
    shade30: colorPrimitive.blue[300],
    shade40: colorPrimitive.blue[600],
    shade50: colorPrimitive.blue[700],
  },
  [PaletteColorsEnum.PURPLE]: {
    shade10: colorPrimitive.purple[100],
    shade20: colorPrimitive.purple[200],
    shade30: colorPrimitive.purple[300],
    shade40: colorPrimitive.purple[600],
    shade50: colorPrimitive.purple[700],
  },
};

/** Main color palette. Dark theme. */
export const paletteDark: Palette = {
  [PaletteColorsEnum.GREY]: {
    shade10: greysDark.shade10,
    shade20: greysDark.shade30,
    shade30: greysDark.shade40,
    shade40: greysDark.shade70,
    shade50: greysDark.shade80,
  },
  [PaletteColorsEnum.PINK]: {
    shade10: colorPrimitive.magenta[1000],
    shade20: colorPrimitive.magenta[900],
    shade30: colorPrimitive.magenta[800],
    shade40: colorPrimitive.magenta[600],
    shade50: colorPrimitive.magenta[500],
  },
  [PaletteColorsEnum.RED]: {
    shade10: colorPrimitive.red[1000],
    shade20: colorPrimitive.red[900],
    shade30: colorPrimitive.red[800],
    shade40: colorPrimitive.red[600],
    shade50: colorPrimitive.red[500],
  },
  [PaletteColorsEnum.ORANGE]: {
    shade10: colorPrimitive.orange[1000],
    shade20: colorPrimitive.orange[900],
    shade30: colorPrimitive.orange[800],
    shade40: colorPrimitive.orange[600],
    shade50: colorPrimitive.orange[500],
  },
  [PaletteColorsEnum.YELLOW]: {
    shade10: colorPrimitive.yellow[1000],
    shade20: colorPrimitive.yellow[900],
    shade30: colorPrimitive.yellow[800],
    shade40: colorPrimitive.yellow[600],
    shade50: colorPrimitive.yellow[500],
  },
  [PaletteColorsEnum.GREEN]: {
    shade10: colorPrimitive.green[1000],
    shade20: colorPrimitive.green[900],
    shade30: colorPrimitive.green[800],
    shade40: colorPrimitive.green[600],
    shade50: colorPrimitive.green[500],
  },
  [PaletteColorsEnum.TEAL]: {
    shade10: colorPrimitive.teal[1000],
    shade20: colorPrimitive.teal[900],
    shade30: colorPrimitive.teal[800],
    shade40: colorPrimitive.teal[600],
    shade50: colorPrimitive.teal[500],
  },
  [PaletteColorsEnum.BLUE]: {
    shade10: colorPrimitive.blue[1000],
    shade20: colorPrimitive.blue[900],
    shade30: colorPrimitive.blue[800],
    shade40: colorPrimitive.blue[600],
    shade50: colorPrimitive.blue[500],
  },
  [PaletteColorsEnum.PURPLE]: {
    shade10: colorPrimitive.purple[1000],
    shade20: colorPrimitive.purple[900],
    shade30: colorPrimitive.purple[800],
    shade40: colorPrimitive.purple[600],
    shade50: colorPrimitive.purple[500],
  },
};

/** @deprecated Check with your designers before using this palette. */
export enum NewPaletteColorsEnum {
  RED = 'red',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  GREEN = 'green',
  BLUE = 'blue',
  GREY = 'grey',
}

export interface NewPaletteColor {
  readonly lowlight: string;
  readonly regular: string;
  readonly highlight: string;
  readonly tint: string;
}

type NewPalette = {readonly [T in NewPaletteColorsEnum]: NewPaletteColor};

/** @deprecated Check with your designers before using this palette. */
export const newPalette: NewPalette = {
  [NewPaletteColorsEnum.RED]: {
    lowlight: '#CC0049',
    regular: '#FF0057',
    highlight: '#FFD4E6',
    tint: '#FFE8F0',
  },
  [NewPaletteColorsEnum.ORANGE]: {
    lowlight: '#DD3C00',
    regular: '#FF5800',
    highlight: '#FFE1CB',
    tint: '#FFF0E3',
  },
  [NewPaletteColorsEnum.YELLOW]: {
    lowlight: '#FFB700',
    regular: '#FFCF00',
    highlight: '#FFFADD',
    tint: '#FFFCEC',
  },
  [NewPaletteColorsEnum.GREEN]: {
    lowlight: '#008B4A',
    regular: '#00D47D',
    highlight: '#ACF8E2',
    tint: '#DCFFF3',
  },
  [NewPaletteColorsEnum.BLUE]: {
    lowlight: '#006EB2',
    regular: '#00BFFF',
    highlight: '#B0F1FC',
    tint: '#D9F8FF',
  },
  [NewPaletteColorsEnum.GREY]: {
    lowlight: '#001B38',
    regular: '#808080',
    highlight: '#F3F2F0',
    tint: '#FAFAFA',
  },
};

export type ColorFinder = (theme: DefaultTheme) => string;
export function resolveColor(color: string | ColorFinder, theme: DefaultTheme) {
  return typeof color === 'function' ? color(theme) : color;
}
